import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  ExperienceAnchorType,
  IEmployeeAppContact,
  IEmployeeAppInstanceNavStyle,
  IEmployeeAppResource,
  IExperienceTemplate,
  PARTICIPANT_ACTOR_KEY
} from '@dadohr/shared/domainTypes/experience';
import {
  IOrganization,
  OrganizationOverrides
} from '@dadohr/shared/domainTypes/org';
import { PaletteColor } from '@dadohr/shared/domainTypes/theme';
import { Theme, useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { Button, IconButton, Radio, Typography } from '@material-ui/core';
import { PlusCircle, Upload, X } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { TextFieldColor } from '../../../../../components/ColorPicker';
import { CustomInputLabel } from '../../../../../components/CustomInputLabel';
import { ImageUploadDialog } from '../../../../../components/ImageUpload';
import { LinkExternal } from '../../../../../components/LinkExternal';
import { TokenDefinition } from '../../../../../components/MarkdownText';
import { SwitchWithLabel } from '../../../../../components/SwitchWithLabel';
import { TextFieldWithReplacementTokens } from '../../../../../components/TextFieldWithReplacementTokens';
import { useDialogState } from '../../../../../hooks/useDialogState';
import { useUnsafeTranslation } from '../../../../../i18next';
import {
  FlexContainer,
  FlexContainerVertical
} from '../../../../../layout/Flex';
import { Section } from '../../../../../layout/Section';
import { useRoutes } from '../../../../../routes';
import { useCurrentUser } from '../../../../../services/currentUser';
import { updateDataInDoc } from '../../../../../services/firebase';
import { ImageUrl, storeImage } from '../../../../Org/pages/Branding';
import { useActorDescriptorsFromTemplate } from '../../../components/Actors';
import { useTokenDefinitionsForExperience } from '../../../components/Builders/TaskBuilder/components/BlockSettings/common';
import { StandardFooter } from '../Footer';
import { StepBodyProps } from '../types';

const ContactGrid = styled('div')((p) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr 1fr max-content',
  gridColumnGap: p.theme.spacing(2),
  width: '100%'
}));

const ContactEditRow = ({
  value,
  onChange,
  onRemove,
  tokens
}: {
  value: IEmployeeAppContact;
  onChange: (nextValue: IEmployeeAppContact) => void;
  onRemove: () => void;
  tokens: TokenDefinition[];
}) => {
  return (
    <ContactGrid>
      <TextFieldWithReplacementTokens
        label="Name"
        fullWidth
        value={value.name}
        onChange={(nv) =>
          onChange({
            ...value,
            name: nv
          })
        }
        tokens={tokens}
      />
      <TextFieldWithReplacementTokens
        label="Description"
        fullWidth
        value={value.description}
        onChange={(nv) =>
          onChange({
            ...value,
            description: nv
          })
        }
        tokens={tokens}
      />
      <TextFieldWithReplacementTokens
        label="Email"
        fullWidth
        value={value.contacts.email}
        onChange={(nv) =>
          onChange({
            ...value,
            contacts: {
              ...value.contacts,
              email: nv
            }
          })
        }
        tokens={tokens}
      />
      <IconButton onClick={onRemove}>
        <X size={16} />
      </IconButton>
    </ContactGrid>
  );
};

const ResourceGrid = styled('div')((p) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr max-content 1fr',
  gridColumnGap: p.theme.spacing(2),
  width: '100%'
}));

const ResourceEditRow = ({
  value,
  onChange,
  onRemove,
  tokens
}: {
  value: IEmployeeAppResource;
  onChange: (nextValue: IEmployeeAppResource) => void;
  onRemove: () => void;
  tokens: TokenDefinition[];
}) => {
  if (value.type === 'LINK') {
    return (
      <ResourceGrid>
        <TextFieldWithReplacementTokens
          label="Name"
          fullWidth
          value={value.label}
          onChange={(nv) => onChange({ ...value, label: nv })}
          tokens={tokens}
        />
        <TextFieldWithReplacementTokens
          label="URL"
          fullWidth
          value={value.url}
          placeholder="https://..."
          onChange={(nv) => onChange({ ...value, url: nv })}
          tokens={tokens}
        />
        <IconButton onClick={onRemove}>
          <X size={16} />
        </IconButton>
        <div />
      </ResourceGrid>
    );
  }
  return null;
};

const updateEmployeeAppProps = (
  d: Doc<IExperienceTemplate>,
  update: Partial<IExperienceTemplate['employeeApp']>
) => {
  return updateDataInDoc(d, {
    employeeApp: {
      ...d.data.employeeApp,
      ...update
    }
  });
};

const updateOrgOverrides = (
  d: Doc<IExperienceTemplate>,
  update: Partial<OrganizationOverrides>
) => {
  return updateDataInDoc(d, {
    overrides: {
      ...(d.data.overrides || {}),
      org: {
        ...(d.data.overrides?.org || {}),
        ...update
      }
    }
  });
};

const updatePrimaryColors = (
  d: Doc<IExperienceTemplate>,
  theme: Theme,
  update: Partial<PaletteColor>
) => {
  return updateDataInDoc(d, {
    overrides: {
      ...(d.data.overrides || {}),
      org: {
        ...(d.data.overrides?.org || {}),
        experienceTheming: {
          ...(d.data.overrides?.org?.experienceTheming || {}),
          colors: {
            ...(d.data.overrides?.org?.experienceTheming?.colors || {}),
            primary: {
              ...theme.custom.colors.primary,
              ...(d.data.overrides?.org?.experienceTheming?.colors?.primary ||
                {}),
              ...update
            }
          }
        }
      }
    }
  });
};

const updateHeaderBackgroundDark = (
  d: Doc<IExperienceTemplate>,
  theme: Theme,
  update: {
    main: string;
  }
) => {
  return updateDataInDoc(d, {
    overrides: {
      ...(d.data.overrides || {}),
      org: {
        ...(d.data.overrides?.org || {}),
        experienceTheming: {
          ...(d.data.overrides?.org?.experienceTheming || {}),
          colors: {
            ...(d.data.overrides?.org?.experienceTheming?.colors || {}),
            headerBackgroundDark: {
              ...theme.custom.colors.headerBackgroundDark,
              ...(d.data.overrides?.org?.experienceTheming?.colors
                ?.headerBackgroundDark || {}),
              ...update
            }
          }
        }
      }
    }
  });
};

const RemoveButton = ({ onClick }: { onClick: () => void }) => {
  const { t } = useUnsafeTranslation();
  return (
    <Button color="secondary" variant="text" onClick={onClick} size="small">
      {t('Remove')}
    </Button>
  );
};

const BannerImageUrl = ({
  value,
  onChange
}: {
  value: string;
  onChange: (nextValue: string) => void;
}) => {
  const { t } = useUnsafeTranslation();
  const { dialogOpen, openDialog, closeDialog } = useDialogState(false);
  return (
    <>
      <FlexContainerVertical spacing={1} fullWidth>
        {value && <img src={value} height={8 * 10} alt="" />}
        <FlexContainer>
          <Button
            startIcon={<Upload />}
            variant="outlined"
            onClick={openDialog}
          >
            {t('Upload image')}
          </Button>
          {value && <RemoveButton onClick={() => onChange('')} />}
        </FlexContainer>
        <Typography variant="caption">{t('PNG, SVG or JPG.')}</Typography>
      </FlexContainerVertical>
      <ImageUploadDialog
        title={t('Upload banner image')}
        open={dialogOpen}
        onCancel={closeDialog}
        src={value}
        onUpload={async (blob) => {
          const url = await storeImage(blob);
          onChange(url);
        }}
      />
    </>
  );
};

const getDefaultInstancePageNavStyle = (
  tpl: Doc<IExperienceTemplate>,
  actorKey: string
) => {
  if (
    actorKey === PARTICIPANT_ACTOR_KEY &&
    tpl.data.anchor.type === ExperienceAnchorType.EMPLOYEE_DATE
  ) {
    return IEmployeeAppInstanceNavStyle.WEEKS;
  }
  return IEmployeeAppInstanceNavStyle.INBOX;
};

const Table = styled('table')((p) => ({
  borderSpacing: 0,
  td: {
    paddingRight: p.theme.spacing(3)
  },
  'thead > td': {
    paddingTop: p.theme.spacing(1),
    paddingBottom: p.theme.spacing(1)
  },
  'tbody > tr': {
    ':hover': {
      backgroundColor: p.theme.custom.colors.gray.w100
    },
    verticalAlign: 'middle',
    td: {
      paddingTop: p.theme.spacing(1),
      paddingBottom: p.theme.spacing(1)
    }
  }
}));

const NavStyleSelectorTable = ({
  d,
  onChange: _onChange
}: Pick<StepBodyProps<Doc<IExperienceTemplate>>, 'd' | 'onChange'>) => {
  const { t } = useUnsafeTranslation();
  const actorDescriptors = useActorDescriptorsFromTemplate(
    d.data.supportingActors
  );
  const actors = Object.values(actorDescriptors);
  const v = d.data.employeeApp.instancePageNavStylePerActor;
  const getValue = (actorKey: string) =>
    v[actorKey] || getDefaultInstancePageNavStyle(d, actorKey);
  const onChange = (
    actorKey: string,
    nextValue: IEmployeeAppInstanceNavStyle
  ) =>
    _onChange((x) =>
      updateEmployeeAppProps(x, {
        instancePageNavStylePerActor: {
          ...x.data.employeeApp.instancePageNavStylePerActor,
          [actorKey]: nextValue
        }
      })
    );
  return (
    <Table>
      <thead>
        <td style={{ minWidth: 200 }} />
        <td style={{ maxWidth: 130 }}>{t('Organize tasks by week')}</td>
        <td style={{ maxWidth: 130 }}>{t('Organize tasks by status')}</td>
      </thead>
      <tbody>
        {actors.map((a) => (
          <tr key={a.key}>
            <td>{a.label}</td>
            <td>
              <Radio
                color="primary"
                checked={getValue(a.key) === IEmployeeAppInstanceNavStyle.WEEKS}
                onChange={(ev) =>
                  ev.target.checked &&
                  onChange(a.key, IEmployeeAppInstanceNavStyle.WEEKS)
                }
              />
            </td>
            <td>
              <Radio
                color="primary"
                checked={getValue(a.key) === IEmployeeAppInstanceNavStyle.INBOX}
                onChange={(ev) =>
                  ev.target.checked &&
                  onChange(a.key, IEmployeeAppInstanceNavStyle.INBOX)
                }
              />
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
};

const isUsingOrgTheming = (d: Doc<IExperienceTemplate>, org: IOrganization) => {
  const overrides = d.data.overrides?.org || null;
  if (!overrides) {
    return true;
  }
  // Initially thought that we'd change here if the overrides are the same as in the org.
  // But let's not do that - the initial state for getting custom would equal this state.
  // Instead, we'll just check if the overrides are null and need to make sure that for all
  // where we want the default to be used, that the overrides are nulled out.
  return false;
};

export const StepEmployeeApp = ({
  d,
  label,
  onChange,
  goTo,
  nextStep,
  onCheckChanges,
  onLaunchFromRoughDraft,
  isLaunchReady,
  onLaunch,
  lastSavedAt
}: StepBodyProps<Doc<IExperienceTemplate>>) => {
  const { t } = useUnsafeTranslation();
  const { org } = useCurrentUser();
  const e = d.data.employeeApp;
  const { ROUTES } = useRoutes();

  const theme = useTheme();

  const usesOrgTheming = isUsingOrgTheming(d, org);
  // Keep the previous state, so that if someone is just toggling the default theming
  // switch, the underlying data is not lost.
  const prevTheming = useRef(d.data.overrides?.org || null);
  useEffect(() => {
    if (d.data.overrides?.org) {
      prevTheming.current = d.data.overrides?.org;
    }
  }, [d.data.overrides?.org || null]);

  const tplTheming = d.data.overrides?.org?.experienceTheming;
  const orgTheming = org.experienceTheming;
  const primaryColors =
    tplTheming?.colors?.primary ||
    orgTheming?.colors?.primary ||
    theme.custom.colors.primary;

  const headerBackgroundDarkColors = {
    main:
      tplTheming?.colors?.headerBackgroundDark?.main ||
      orgTheming?.colors?.headerBackgroundDark?.main ||
      theme.custom.colors.headerBackgroundDark.main
  };

  const tokens = useTokenDefinitionsForExperience(
    d.data.supportingActors,
    org.customUserFields,
    {
      orgId: org.id,
      experienceTemplateId: d.id
    }
  );
  return (
    <>
      <Section>
        <FlexContainerVertical spacing={3} fullWidth>
          <Typography component="h1" variant="h3">
            {label}
          </Typography>

          <FlexContainerVertical spacing={3} fullWidth>
            <Typography variant="h4">{t('Branding options')}</Typography>

            <FlexContainerVertical spacing={1} fullWidth>
              <SwitchWithLabel
                label={
                  <strong>{t('Use organization default branding')}</strong>
                }
                checked={usesOrgTheming}
                onChange={(nextChecked) => {
                  if (nextChecked) {
                    onChange((x) =>
                      updateDataInDoc(x, {
                        overrides: {
                          ...(x.data.overrides || {}),
                          org: null
                        }
                      })
                    );
                  } else {
                    onChange((x) =>
                      updateDataInDoc(x, {
                        overrides: {
                          ...(x.data.overrides || {}),
                          org: prevTheming.current || {
                            logoUrl: org.logoUrl,
                            logoInvertedUrl: org.logoInvertedUrl,
                            experienceTheming: {
                              colors: {
                                primary:
                                  orgTheming?.colors?.primary ||
                                  theme.custom.colors.primary,
                                headerBackgroundDark:
                                  orgTheming?.colors?.headerBackgroundDark ||
                                  theme.custom.colors.headerBackgroundDark
                              }
                            }
                          }
                        }
                      })
                    );
                  }
                }}
              />

              {usesOrgTheming && (
                <Typography color="textSecondary">
                  {t('Organization branding can be found in')}{' '}
                  <LinkExternal
                    href={ROUTES.org.branding.url()}
                    color="primary"
                  >
                    {t('Settings')}
                  </LinkExternal>
                </Typography>
              )}
            </FlexContainerVertical>

            {!usesOrgTheming && (
              <>
                <CustomInputLabel
                  label={t('Logo for email notifications')}
                  subLabel={t('Shown on a white background')}
                >
                  <ImageUrl
                    value={d.data.overrides?.org?.logoUrl || ''}
                    onChange={(nextUrl) => {
                      onChange((x) =>
                        updateOrgOverrides(x, {
                          logoUrl: nextUrl
                        })
                      );
                    }}
                    defaultValue={org.logoUrl}
                    backgroundColor={'#ffffff'}
                  />
                </CustomInputLabel>

                <CustomInputLabel
                  label={t('Logo for Experience Hub header')}
                  subLabel={t(
                    'Shown on a colored background, specified as header background color below'
                  )}
                >
                  <ImageUrl
                    value={d.data.overrides?.org?.logoInvertedUrl || ''}
                    backgroundColor={headerBackgroundDarkColors.main}
                    onChange={(nextUrl) => {
                      onChange((x) =>
                        updateOrgOverrides(x, {
                          logoInvertedUrl: nextUrl
                        })
                      );
                    }}
                    defaultValue={org.logoInvertedUrl || org.logoUrl}
                  />
                </CustomInputLabel>

                <CustomInputLabel
                  label={t('Background image')}
                  subLabel={t('Shown in the header of the Experience Hub')}
                >
                  <BannerImageUrl
                    value={e.banner.url}
                    onChange={(nextUrl) => {
                      onChange((x) =>
                        updateEmployeeAppProps(x, {
                          banner: {
                            ...x.data.employeeApp.banner,
                            url: nextUrl
                          }
                        })
                      );
                    }}
                  />
                </CustomInputLabel>

                <FlexContainerVertical spacing={2} fullWidth>
                  <Typography variant="h5">{t('Colors')}</Typography>
                  <CustomInputLabel
                    label={t('Header background color')}
                    subLabel={t('Used for header banner on Experience Hub')}
                  >
                    <TextFieldColor
                      value={headerBackgroundDarkColors.main}
                      onChange={(nextColor) =>
                        onChange((x) =>
                          updateHeaderBackgroundDark(x, theme, {
                            main: nextColor
                          })
                        )
                      }
                    />
                  </CustomInputLabel>
                  <CustomInputLabel
                    label={t('Main color')}
                    subLabel={t(
                      'Used for buttons and other highlights on Experience Hub'
                    )}
                  >
                    <TextFieldColor
                      value={primaryColors.main}
                      onChange={(nextColor) =>
                        onChange((x) =>
                          updatePrimaryColors(x, theme, {
                            main: nextColor
                          })
                        )
                      }
                    />
                  </CustomInputLabel>
                  <CustomInputLabel
                    label={t('Darker color')}
                    subLabel={t(
                      'A darker shade of the main color, used for hover states'
                    )}
                  >
                    <TextFieldColor
                      value={primaryColors.dark}
                      onChange={(nextColor) =>
                        onChange((x) =>
                          updatePrimaryColors(x, theme, {
                            dark: nextColor
                          })
                        )
                      }
                    />
                  </CustomInputLabel>
                  <CustomInputLabel
                    label={t('Lighter color')}
                    subLabel={t(
                      'A lighter shade of the main color, used for hover states'
                    )}
                  >
                    <TextFieldColor
                      value={primaryColors.light}
                      onChange={(nextColor) =>
                        onChange((x) =>
                          updatePrimaryColors(x, theme, {
                            light: nextColor
                          })
                        )
                      }
                    />
                  </CustomInputLabel>

                  <CustomInputLabel
                    label={t('Text color')}
                    subLabel={t(
                      'When text appears on a background that is the main color set above'
                    )}
                  >
                    <TextFieldColor
                      value={primaryColors.contrastText}
                      onChange={(nextColor) =>
                        onChange((x) =>
                          updatePrimaryColors(x, theme, {
                            contrastText: nextColor
                          })
                        )
                      }
                    />
                  </CustomInputLabel>
                </FlexContainerVertical>
              </>
            )}
          </FlexContainerVertical>

          <FlexContainerVertical spacing={1} fullWidth>
            <Typography variant="h4">{t('Navigation')}</Typography>
            <LinkExternal
              color="primary"
              href="https://help.dadohr.com/article/153-employee-view-options#Navigation-organized-by-week-or-organized-by-status-Ynr2H"
            >
              {t('Learn more about these options')}
            </LinkExternal>
            <NavStyleSelectorTable d={d} onChange={onChange} />
          </FlexContainerVertical>
          <FlexContainerVertical spacing={1} fullWidth>
            <Typography variant="h4">{t('Contacts')}</Typography>
            {!!e.contacts.length && (
              <FlexContainerVertical spacing={1} fullWidth>
                {e.contacts.map((c, i) => (
                  <ContactEditRow
                    key={i}
                    value={c}
                    onChange={(nextContact) =>
                      onChange((x) =>
                        updateEmployeeAppProps(x, {
                          contacts: x.data.employeeApp.contacts.map((cc, ii) =>
                            ii === i ? nextContact : cc
                          )
                        })
                      )
                    }
                    onRemove={() =>
                      onChange((x) =>
                        updateEmployeeAppProps(x, {
                          contacts: x.data.employeeApp.contacts.filter(
                            (cc, ii) => ii !== i
                          )
                        })
                      )
                    }
                    tokens={tokens}
                  />
                ))}
              </FlexContainerVertical>
            )}
            <Button
              color="primary"
              size="small"
              startIcon={<PlusCircle size={16} />}
              onClick={() => {
                onChange((x) =>
                  updateEmployeeAppProps(x, {
                    contacts: [
                      ...x.data.employeeApp.contacts,
                      {
                        name: '',
                        description: '',
                        contacts: {
                          email: ''
                        }
                      }
                    ]
                  })
                );
              }}
            >
              {t('Add contact')}
            </Button>
          </FlexContainerVertical>

          <FlexContainerVertical spacing={1}>
            <Typography variant="h4">{t('Resources')}</Typography>
            {!!e.resources.length && (
              <FlexContainerVertical spacing={1}>
                {e.resources.map((r, i) => (
                  <ResourceEditRow
                    key={i}
                    value={r}
                    onChange={(nextResource) =>
                      onChange((x) =>
                        updateEmployeeAppProps(x, {
                          resources: x.data.employeeApp.resources.map(
                            (rr, ii) => (ii === i ? nextResource : rr)
                          )
                        })
                      )
                    }
                    onRemove={() =>
                      onChange((x) =>
                        updateEmployeeAppProps(x, {
                          resources: x.data.employeeApp.resources.filter(
                            (rr, ii) => ii !== i
                          )
                        })
                      )
                    }
                    tokens={tokens}
                  />
                ))}
              </FlexContainerVertical>
            )}
            <Button
              color="primary"
              size="small"
              startIcon={<PlusCircle size={16} />}
              onClick={() => {
                onChange((x) =>
                  updateEmployeeAppProps(x, {
                    resources: [
                      ...x.data.employeeApp.resources,
                      {
                        type: 'LINK',
                        label: '',
                        url: ''
                      }
                    ]
                  })
                );
              }}
            >
              {t('Add resource')}
            </Button>
          </FlexContainerVertical>
        </FlexContainerVertical>
        <StandardFooter d={d} onCheckChanges={onCheckChanges} onLaunchFromRoughDraft={onLaunchFromRoughDraft} isLaunchReady={isLaunchReady} onLaunch={onLaunch} lastSavedAt={lastSavedAt} />
      </Section>
    </>
  );
};
